import styled from "@emotion/styled";
import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Add } from "@livechat/design-system-icons";
import { Button, Icon, Info, SpacingToken, Tooltip } from "@livechat/design-system-react-components";

import { KeyModifier, Shortcuts } from "components/shortcuts/Shortcuts";
import { KeyCodeEnum } from "constants/keyCode";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { actions$ } from "store/action-stream";
import { RequestAction } from "store/entities/actions";
import { ITag } from "store/entities/tags";
import { selectIsTagReminderEnabled } from "store/entities/teams";
import { TICKET } from "store/entities/tickets/actions";
import { IActionWithPayload } from "store/helper";
import { ITicketsViewSendMessageSuccessPayload } from "store/views/tickets/interfaces";
import { withVar } from "themes/helpers";

import { TagBadges } from "./TagBadges";
import { TagSelect } from "./TagSelect";

interface IProps {
  options: ITag[];
  selectedTags: ITag[];
  canManageTags: boolean;
  disableShortcuts?: boolean;
  ticketID: string;
  onSelect: (tag: string) => void;
  onRemove: (tag: string) => void;
  onAddTagClick?: () => void;
  onAddTagShortcut?: () => void;
  teamID?: string;
  isNewTicketCreatedByAgentOpened?: boolean;
  disableTagReminder?: boolean;
}

export const TagPicker: FC<IProps> = ({
  options,
  selectedTags,
  canManageTags,
  disableShortcuts = false,
  ticketID,
  onSelect,
  onRemove,
  onAddTagClick,
  onAddTagShortcut,
  teamID,
  isNewTicketCreatedByAgentOpened = false,
  disableTagReminder = false,
}) => {
  const isTagReminderEnabled = useSelector(selectIsTagReminderEnabled);

  const shouldRemindAboutTagging =
    canManageTags && !disableTagReminder && isTagReminderEnabled && selectedTags.length === 0;

  const [remindAboutTaggingOnNewMessage, setRemindAboutTaggingOnNewMessage] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showTagReminder, setShowTagReminder] = useState(shouldRemindAboutTagging && isNewTicketCreatedByAgentOpened);

  const shortcutsDisabled = disableShortcuts || !canManageTags;

  const handleHideTagReminder = (): void => {
    setShowTagReminder(false);
  };

  const handleShowTagReminder = (): void => {
    if (shouldRemindAboutTagging) setShowTagReminder(true);
  };

  const handleRemoveTag = (tagId: string): void => {
    if (canManageTags) onRemove(tagId);
  };

  const handleAddTagShortcut = (): void => {
    setExpanded(true);
    onAddTagShortcut?.();
    trackEngagement("Tickets: Shortcut used", EventPlace.Tickets, {
      shortcut: "tag",
    });
  };

  const handleCloseOnEscape = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      setExpanded(false);
    }
  };

  const handleAddTagButtonClick = (): void => {
    setExpanded(true);
    onAddTagClick?.();
  };

  const handleSelect = (tagId: string): void => {
    if (showTagReminder) {
      trackEngagement("Tag reminder: Tag added with reminder shown", EventPlace.Tickets);
      handleHideTagReminder();
    }
    onSelect(tagId);
  };

  const shortcutsConfig = {
    [KeyCodeEnum.KeyM]: {
      additionalKeysRequired: [KeyModifier.Alt, KeyModifier.Control],
      disableDefaultBehavior: true,
      eventHandler: handleAddTagShortcut,
    },
    [KeyCodeEnum.KeyT]: {
      additionalKeysRequired: [KeyModifier.Alt, KeyModifier.Control],
      disableDefaultBehavior: true,
      eventHandler: handleAddTagShortcut,
    },
  };

  useEffect(() => {
    if (remindAboutTaggingOnNewMessage) {
      setRemindAboutTaggingOnNewMessage(false);

      if (shouldRemindAboutTagging) {
        handleShowTagReminder();
      }
    }
  }, [remindAboutTaggingOnNewMessage, selectedTags]);

  useEffect(() => {
    if (!canManageTags) return;

    document.addEventListener("keydown", handleCloseOnEscape);

    return (): void => document.removeEventListener("keydown", handleCloseOnEscape);
  }, []);

  useEffect(() => {
    // We intentionally do not check selectedTags here, because they can change after the component is mounted
    // and we want to avoid unnecessary re-subscriptions as this can lead to race conditions issues
    if (!canManageTags || !isTagReminderEnabled || disableTagReminder) return;

    const reminderUnsubscribe = pipe(
      actions$,
      filter(
        (action: IActionWithPayload<string, ITicketsViewSendMessageSuccessPayload>) =>
          action.type === TICKET.SEND_MESSAGE[RequestAction.SUCCESS] && action.payload.ticket.id === ticketID
      ),
      subscribe(() => {
        setRemindAboutTaggingOnNewMessage(true);
      })
    );

    return (): void => {
      reminderUnsubscribe();
    };
  }, []);

  return (
    <TagBadges tags={selectedTags} onRemove={handleRemoveTag}>
      <Shortcuts config={shortcutsConfig} disableShortcuts={shortcutsDisabled}>
        <Tooltip
          placement="left"
          isVisible={showTagReminder}
          triggerRenderer={() => (
            <AddTagTrigger
              expanded={expanded}
              canAddTags={canManageTags}
              onClick={handleAddTagButtonClick}
              options={options}
              selected={selectedTags}
              onSelect={handleSelect}
              setExpanded={setExpanded}
              teamID={teamID}
            />
          )}
        >
          <Info
            handleCloseAction={handleHideTagReminder}
            header="Don't forget to tag this ticket!"
            text="Tags let you categorize tickets and organize your customers requests. Admins can adjust the display settings for this reminder in the “Tags” settings."
          />
        </Tooltip>
      </Shortcuts>
    </TagBadges>
  );
};

interface IAddTagTriggerProps {
  expanded: boolean;
  canAddTags: boolean;
  onClick: VoidFunction;
  options: ITag[];
  selected: ITag[];
  onSelect: (tag: string) => void;
  setExpanded: (expanded: boolean) => void;
  teamID?: string;
}

const AddTagTrigger: FC<IAddTagTriggerProps> = ({
  expanded,
  canAddTags,
  onClick,
  options,
  selected,
  onSelect,
  setExpanded,
  teamID,
}) => {
  if (expanded) {
    return (
      <TagSelect
        tags={options}
        selected={selected}
        onSelect={onSelect}
        onClickOutside={() => setExpanded(false)}
        teamID={teamID}
      />
    );
  }

  return (
    <AddTagButton
      data-testid="add-tag"
      disabled={!canAddTags}
      onClick={onClick}
      kind="plain"
      size="xcompact"
      icon={<Icon source={Add} />}
    >
      Add tag
    </AddTagButton>
  );
};

const AddTagButton = styled(Button)`
  margin: ${withVar(SpacingToken.Spacing1)} 0;
`;
